import { createContext, useContext, ReactNode } from "react";
import { useMediaQuery } from "@/hooks/use-media-query";

/**
 * Responsive breakpoint values
 */
export interface ResponsiveContextValue {
  /** True if viewport width < 768px (mobile) */
  isMobile: boolean;
  /** True if viewport width >= 768px and < 1024px (tablet) */
  isTablet: boolean;
  /** True if viewport width >= 1024px (desktop) */
  isDesktop: boolean;
  /** True if viewport width >= 1440px (large desktop) */
  isLargeDesktop: boolean;
}

const ResponsiveContext = createContext<ResponsiveContextValue | undefined>(undefined);

/**
 * Provider component that supplies responsive breakpoint state to children
 * 
 * @param children - React children to provide context to
 * 
 * @example
 * ```tsx
 * <ResponsiveProvider>
 *   <App />
 * </ResponsiveProvider>
 * ```
 * 
 * @remarks
 * - Use sparingly - only wrap components that need global responsive state
 * - For component-specific responsive needs, use `useIsMobile()` or `useMediaQuery()` directly
 * - Prevents prop drilling of responsive state
 */
export function ResponsiveProvider({ children }: { children: ReactNode }) {
  // Mobile: < 768px
  const isMobile = useMediaQuery("(max-width: 767px)");
  
  // Tablet: 768px - 1023px
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  
  // Desktop: >= 1024px
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  
  // Large Desktop: >= 1440px
  const isLargeDesktop = useMediaQuery("(min-width: 1440px)");

  const value: ResponsiveContextValue = {
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
  };

  return (
    <ResponsiveContext.Provider value={value}>
      {children}
    </ResponsiveContext.Provider>
  );
}

/**
 * Hook to access responsive breakpoint state from ResponsiveContext
 * 
 * @returns ResponsiveContextValue with isMobile, isTablet, isDesktop, isLargeDesktop
 * @throws Error if used outside of ResponsiveProvider
 * 
 * @example
 * ```tsx
 * const { isMobile, isDesktop } = useResponsive();
 * 
 * return (
 *   <div>
 *     {isMobile ? <MobileLayout /> : <DesktopLayout />}
 *   </div>
 * );
 * ```
 * 
 * @remarks
 * - Only use this hook when you need multiple breakpoint values
 * - For single breakpoint checks, prefer `useIsMobile()` or `useMediaQuery()`
 * - Must be used within a ResponsiveProvider
 */
export function useResponsive(): ResponsiveContextValue {
  const context = useContext(ResponsiveContext);
  
  if (context === undefined) {
    throw new Error("useResponsive must be used within a ResponsiveProvider");
  }
  
  return context;
}

